import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ContentFilterEventItem,
  correlateContentFilterEvent,
  correlateContentFilterEventOptions,
  errorMessage,
} from "@/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/** Where a reveal's result is kept; every row of the event reads it from there and never requests it. */
export const correlationOptions = (eventUuid: string) =>
  correlateContentFilterEventOptions({ path: { event_uuid: eventUuid } });

/**
 * Reveal behind a confirmation (render `dialog` once). The backend audits every call, so it runs as a mutation:
 * only on a confirm, never retried, and finished even if the page goes away (a query would be aborted after the
 * audit). The dialog stays open until it's answered.
 */
export function useReveal() {
  const { t } = useTranslation("adminContentSafety");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<ContentFilterEventItem | null>(null);
  const reveal = useMutation({
    mutationFn: async (eventUuid: string) =>
      (await correlateContentFilterEvent({ path: { event_uuid: eventUuid }, throwOnError: true })).data,
    onSuccess: (correlation, eventUuid) =>
      queryClient.setQueryData(correlationOptions(eventUuid).queryKey, correlation),
    onError: (error) => toast.error(errorMessage(error, t("reveal.failed"))),
    onSettled: () => setAsking(null),
  });

  const dialog = asking && (
    <ConfirmDialog
      open
      title={t("reveal.title")}
      body={t("reveal.body")}
      confirmLabel={t("reveal.confirm")}
      loading={reveal.isPending}
      onConfirm={() => reveal.mutate(asking.eventUuid)}
      onClose={() => {
        if (!reveal.isPending) setAsking(null);
      }}
    />
  );

  return { ask: setAsking, dialog };
}
